import { useState, useCallback, useMemo } from 'react';

type UseSelectionOptions<T> = {
  /** Function to extract unique ID from an item */
  getId: (item: T) => string;
  /** Initial selected IDs */
  initialSelectedIds?: string[];
};

type UseSelectionReturn<T> = {
  /** Set of currently selected IDs */
  selectedIds: Set<string>;
  /** Toggle selection of an item by ID */
  toggle: (id: string) => void;
  /** Select all items from provided list */
  selectAll: (items: T[]) => void;
  /** Deselect all items */
  deselectAll: () => void;
  /** Check if an item is selected */
  isSelected: (id: string) => boolean;
  /** Replace entire selection with new set */
  setSelection: (ids: string[]) => void;
};

/**
 * Custom hook for managing selection state with a Set of IDs.
 * Useful for multi-select UIs where items can be toggled, selected all, or deselected.
 */
export function useSelection<T>({
  getId,
  initialSelectedIds = [],
}: UseSelectionOptions<T>): UseSelectionReturn<T> {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(initialSelectedIds),
  );

  const toggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(
    (items: T[]) => {
      setSelectedIds(new Set(items.map(getId)));
    },
    [getId],
  );

  const deselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const isSelected = useCallback(
    (id: string) => selectedIds.has(id),
    [selectedIds],
  );

  const setSelection = useCallback((ids: string[]) => {
    setSelectedIds(new Set(ids));
  }, []);

  return useMemo(
    () => ({
      selectedIds,
      toggle,
      selectAll,
      deselectAll,
      isSelected,
      setSelection,
    }),
    [selectedIds, toggle, selectAll, deselectAll, isSelected, setSelection],
  );
}
